<script>
	import { base } from '$app/paths';
	import SeoHead from '../../../common/SeoHead.svelte';
	import guides from './guides.json';
	const origin = `https://tools.d-cine.net${base}`;
	const structuredData = JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'CollectionPage',
		name: 'KdmGen Walk Throughs/Help',
		url: `${origin}/kdmgen/help/`,
		description:
			'Illustrated manuals for cinema device certificates, DCP/CPL registration, KDM generation and Facilities/OpenTDL.',
		mainEntity: {
			'@type': 'ItemList',
			itemListElement: guides.map((guide, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name: guide.heading,
				url: `${origin}/kdmgen/help/${guide.slug}/`
			}))
		}
	}).replace(/</g, '\\u003c');
</script>

<SeoHead
	title="KdmGen walkthroughs and help"
	description="Illustrated KdmGen user manuals: find device certificates, register encrypted DCPs and CPLs, generate KDMs and maintain cinema facilities in OpenTDL."
	path="/kdmgen/help/"
	image="/kdmgen-help/device-certs-choose-certificate-type.webp"
/>
<svelte:head>{@html `<script type="application/ld+json">${structuredData}</script>`}</svelte:head>

<main class="help-content">
	<header class="guide-header">
		<nav class="breadcrumbs" aria-label="Breadcrumb">
			<a href={`${base}/kdmgen/welcome/`}>KdmGen</a><span aria-hidden="true">/</span><span
				>Walk Throughs/Help</span
			>
		</nav>
		<p class="guide-kicker">KdmGen user manuals</p>
		<h1>Walk Throughs/Help</h1>
		<p class="guide-lead">
			From the right player certificate to a delivered KDM. Follow the illustrated steps for each
			tool, or work through the complete registration and booking process.
		</p>
	</header>
	<section aria-labelledby="choose-guide">
		<h2 id="choose-guide">Choose your task</h2>
		<div class="guide-index">
			{#each guides as guide, index}
				<article>
					<a
						class="guide-preview"
						href={`${base}/kdmgen/help/${guide.slug}/`}
						aria-label={`Read ${guide.title} walkthrough`}
					>
						<img
							src={`${base}/kdmgen-help/${guide.slug}-${guide.steps[0].id}.webp`}
							alt={guide.steps[0].visual.title +
								': illustrated example of the controls covered by this guide'}
							width="1200"
							height="800"
							loading={index < 2 ? 'eager' : 'lazy'}
						/>
					</a>
					<div class="guide-card-copy">
						<p class="guide-meta">{guide.steps.length} illustrated steps · {guide.time}</p>
						<h3><a href={`${base}/kdmgen/help/${guide.slug}/`}>{guide.title}</a></h3>
						<p>{guide.intro}</p>
						<a class="guide-link" href={`${base}/kdmgen/help/${guide.slug}/`}
							>Read the walkthrough <i class="bx bx-right-arrow-alt" aria-hidden="true"></i></a
						>
					</div>
				</article>
			{/each}
		</div>
	</section>
	<section class="help-band" aria-labelledby="start-to-finish">
		<h2 id="start-to-finish">Your first KDM, from start to finish</h2>
		<ol class="workflow-path">
			<li>
				<strong>Prepare the recipients</strong>
				<p>Find player certificates, or check the cinema and screens in OpenTDL.</p>
				<a href={`${base}/kdmgen/help/device-certs/`}>Device Certs guide</a>
			</li>
			<li>
				<strong>Register the content</strong>
				<p>
					Download the backend public certificate, obtain matching DKDMs and register the encrypted
					CPLs.
				</p>
				<a href={`${base}/kdmgen/help/register-dcp-cpl/`}>Registration guide</a>
			</li>
			<li>
				<strong>Create and deliver</strong>
				<p>
					Select CPLs, dates and targets. Validate the setup, generate the KDMs and check delivery.
				</p>
				<a href={`${base}/kdmgen/help/generate-kdm/`}>Generation guide</a>
			</li>
		</ol>
	</section>
	<section class="help-band" aria-labelledby="glossary">
		<h2 id="glossary">The terms you will see</h2>
		<dl class="help-glossary">
			<div>
				<dt>DCP</dt>
				<dd>
					Digital Cinema Package: the package of picture, sound and other material used for cinema
					playback. It can contain more than one composition.
				</dd>
			</div>
			<div>
				<dt>CPL</dt>
				<dd>
					Composition Playlist: identifies one composition and how its assets are played. Its UUID
					identifies the exact version a DKDM must match.
				</dd>
			</div>
			<div>
				<dt>DKDM</dt>
				<dd>
					A distribution KDM addressed to the backend. It provides the authorised keys and validity
					from which this service can create recipient-player KDMs.
				</dd>
			</div>
			<div>
				<dt>KDM</dt>
				<dd>
					Key Delivery Message: allows the targeted player to use the keys for a particular
					encrypted composition during the authorised period.
				</dd>
			</div>
			<div>
				<dt>Public device certificate</dt>
				<dd>
					Identifies the recipient player. It is supplied to the KDM issuer; the player's private
					key stays with the player.
				</dd>
			</div>
			<div>
				<dt>OpenTDL / FLM</dt>
				<dd>
					OpenTDL connects facilities, auditoriums and devices. An FLM (Facility List Message)
					represents the facility information in a structured XML message.
				</dd>
			</div>
			<div>
				<dt>FFMC / FFEC</dt>
				<dd>
					First Frame of Moving Credits / First Frame of End Credits. Where present in a CPL, these
					markers can supply the suggested credits offset in KDM emails.
				</dd>
			</div>
		</dl>
	</section>
	<aside class="help-support">
		<strong>Need help with a specific result?</strong>
		<p>
			Use the troubleshooting section in the relevant guide. When contacting <a
				href="mailto:support@d-cine.net">support@d-cine.net</a
			>, include the tool name, CPL UUID or facility identity, and the exact error text. The step
			illustrations use fictional example data; screenshots are labelled separately.
		</p>
	</aside>
</main>
